import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Backdrop,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
  Paper,
  AlertTitle,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ConfigContext from "../../common/context/ConfigContext";
import bkstApi from "../../common/api";
import BDatePicker from "../../common/component/BDatePicker";
import { shouldDisableDate, getDateRules, getAvailableTimeslotsbyDate } from "../../common/util";
import moment from "moment";
import currency from "currency.js";
import AddressAutocomplete from "../../common/component/AddressAutocomplete";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";

export default function FulfillmentOptionsV2(props) {
  const {
    cartId,
    items,
    options,
    fulfillment,
    defaultDate,
    onChange,
    onDeliveryPayBlockedChange,
    disableDate = false,
  } = props;
  const siteConfig = useContext(ConfigContext);
  const theme = useTheme();

  // Autocomplete state = persisted fulfillment.location (same object shape from AddressAutocomplete)
  const [address, setAddress] = useState(() =>
    fulfillment.shipping === "delivery" && fulfillment.location?.street ? fulfillment.location : ""
  );

  const [provider, setProvider] = useState("");
  const [quote, setQuote] = useState(options.shipping.delivery?.price || "");
  const [error, setError] = useState("");

  const [forceDeliveryForm, setForceDeliveryForm] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [timeslots, setTimeslots] = useState([]);

  const pickupLocations = options?.shipping?.pickup?.locations ?? [];
  const deliveryTimeslots = options.shipping.delivery?.timeslots || fulfillment.location?.timeslots;
  const deliveryRules = options.shipping.delivery?.rules || fulfillment.location?.rules;

  const persistDeliveryAddress = (nextAddress) => {
    const hasStreet = Boolean(nextAddress?.street);
    setAddress(hasStreet ? nextAddress : "");
    onChange({
      ...fulfillment,
      shipping: "delivery",
      quote: undefined,
      location: {
        ...(hasStreet ? nextAddress : {}),
        ...(deliveryTimeslots ? { timeslots: deliveryTimeslots } : {}),
        ...(deliveryRules ? { rules: deliveryRules } : {}),
      },
    });
  };

  const matchPickupLocation = (l, selected) =>
    selected?.id
      ? l.id === selected.id
      : Boolean(selected?.street) &&
        l.street === selected.street &&
        l.city === selected.city &&
        l.zip === selected.zip;

  const switchToPickup = () => {
    let location;
    if (pickupLocations.length === 1) {
      location = pickupLocations[0];
    } else if (fulfillment.shipping === "pickup") {
      location = pickupLocations.find((l) => matchPickupLocation(l, fulfillment.location)) || null;
    } else {
      location = null;
    }
    onChange({
      ...fulfillment,
      shipping: "pickup",
      location,
      date: defaultDate || "",
      time: "",
    });
    handleClose();
  };

  const resetQuote = () => {
    setProvider("");
    setQuote("");
    setError("");
  };

  const onSubmit = (e) => {
    e.preventDefault();
    resetQuote();

    if (address) {
      if (!provider) {
        // get quote from fulfillment provider
        setProcessing(true);
        bkstApi
          .post(`/site/${siteConfig.id}/cart/${cartId}/delivery`, {
            address,
            date: fulfillment.date,
            time: fulfillment.time,
          })
          .then((res) => {
            setQuote(res.data.quote);
            setProvider(res.data.provider);
            setProcessing(false);
          })
          .catch((err) => {
            setError("Sorry! Delivery option is not available for this date/time & address");
            if (err?.response?.data?.msg) {
              setError(err.response.data.msg);
            }
            setProcessing(false);
          });
      } else {
        onChange({
          ...fulfillment,
          shipping: "delivery",
          quote,
          provider,
          location: {
            ...address,
            ...(deliveryTimeslots ? { timeslots: deliveryTimeslots } : {}),
            ...(deliveryRules ? { rules: deliveryRules } : {}),
          },
        });
        handleClose();
      }
    }
  };

  const handleClose = () => {
    resetQuote();
    setForceDeliveryForm(false);
  };

  useEffect(() => {
    if (fulfillment.location && fulfillment.date) {
      resetQuote();

      // check if date is disabled
      if (shouldDisableDate(siteConfig.id, fulfillment.date, items, fulfillment)) {
        setTimeslots([]);
      } else {
        const slotsbyDate = getAvailableTimeslotsbyDate(
          siteConfig.id,
          fulfillment.location,
          fulfillment.date,
          dateRules,
          fulfillment.shipping
        );
        setTimeslots(slotsbyDate);
        //setTime(slotsbyDate[(slotsbyDate.length / 2) | 0]);
      }
    }
  }, [fulfillment.location, fulfillment.date]);

  // Autocomplete mirrors persisted delivery location (including empty after clear)
  useEffect(() => {
    if (fulfillment.shipping !== "delivery") {
      setAddress("");
      return;
    }
    setAddress(fulfillment.location?.street ? fulfillment.location : "");
  }, [fulfillment.shipping, fulfillment.location]);

  const dateRules = getDateRules(items, fulfillment.location?.timeslots);

  let CTA = <></>;
  let CONTENT = <></>;

  const DATE_TIME = (
    <Box pt={2}>
      <Grid container spacing={3}>
        <Grid item sm={6} xs={12}>
          <FormControl variant="outlined" fullWidth>
            <BDatePicker
              value={fulfillment?.date || ""}
              label={`Date`}
              onChange={(d) => onChange({ ...fulfillment, date: d, time: "" })}
              disablePast
              required
              disabled={disableDate}
              shouldDisableDate={(d) => shouldDisableDate(siteConfig.id, d, items, fulfillment)}
            />
            <FormHelperText>
              {fulfillment.date && moment(fulfillment.date).format("dddd")}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid item sm={6} xs={12}>
          <FormControl
            variant="outlined"
            fullWidth
            required
            error={Boolean(fulfillment?.date && timeslots.length === 0)}
          >
            <InputLabel id="time-label">{`Time`}</InputLabel>
            <Select
              value={fulfillment.time || ""}
              labelId="time-label"
              label={`Time`}
              id="time"
              name="time"
              onChange={(e) => onChange({ ...fulfillment, time: e.target.value })}
            >
              {timeslots.map((i) => (
                <MenuItem key={i} value={i}>
                  {i}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {fulfillment?.date && timeslots.length === 0
                ? "No available timeslots for this date"
                : ""}
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  const deliveryProvider = options.shipping.delivery?.provider;
  const isDoorDash =
    options.shipping.delivery?.type === "third-party" &&
    (deliveryProvider === "doordash" || deliveryProvider?.name === "doordash");

  if (provider) {
    CTA = (
      <Button type="submit" fullWidth variant="contained" color="primary">
        ADD DELIVERY
      </Button>
    );
    CONTENT = (
      <Box mt={2}>
        <Alert severity="success">
          <AlertTitle>Delivery is available!</AlertTitle>Verify address & delivery fee below, then
          click the Add Delivery button
        </Alert>
        <Box mt={3}>
          <Grid container>
            <Grid item xs={8}>
              <Typography variant="overline" color="textSecondary">
                ADDRESS
              </Typography>
              <Typography variant="body1">{address.street}</Typography>
              {address.unit && <Typography variant="body1">{address.unit}</Typography>}
              <Typography variant="body1">
                {address.city}, {address.state}, {address.zip}
              </Typography>
              <Link
                size="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  setProvider("");
                  setQuote("");
                  persistDeliveryAddress("");
                }}
              >
                change
              </Link>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="overline" color="textSecondary">
                DELIVERY FEE
              </Typography>
              <Typography variant="h3">{currency(quote).format()}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  } else {
    CTA = (
      <Button type="submit" fullWidth variant="contained" color="primary">
        CHECK AVAILABILITY
      </Button>
    );
    CONTENT = (
      <Box>
        <Box mb={2}>{DATE_TIME}</Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AddressAutocomplete
              key={address?.street || address?.description || "delivery-address"}
              required
              value={address}
              onPlaceSelect={(o) => {
                setProvider("");
                setQuote("");
                persistDeliveryAddress(o);
              }}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }

  const deliveryConfigured =
    fulfillment.shipping === "delivery" &&
    Boolean(fulfillment.location?.street) &&
    fulfillment.quote != null;
  const showDeliveryForm = !deliveryConfigured || forceDeliveryForm;

  const deliveryActions = (
    <Box mt={2} display="flex" flexDirection="row" gap={1.5} sx={{ width: "100%" }}>
      <Button
        type="button"
        variant="outlined"
        color="secondary"
        fullWidth
        sx={{ flex: "1 1 50%", minWidth: 0 }}
        onClick={switchToPickup}
      >
        Switch to Pickup
      </Button>
      <Box sx={{ flex: "1 1 50%", minWidth: 0, display: "flex" }}>{CTA}</Box>
    </Box>
  );

  useEffect(() => {
    if (!onDeliveryPayBlockedChange) {
      return;
    }
    if (fulfillment.shipping !== "delivery") {
      onDeliveryPayBlockedChange(false);
      return;
    }
    if (isDoorDash) {
      onDeliveryPayBlockedChange(true);
      return;
    }
    const missingDateOrTime = !fulfillment.date || !fulfillment.time;
    const missingQuote = fulfillment.quote == null;
    const hasCheckError = Boolean(error);
    const addressMissingInForm =
      showDeliveryForm && !(address && typeof address === "object" && address.street);
    onDeliveryPayBlockedChange(
      missingDateOrTime || missingQuote || hasCheckError || addressMissingInForm
    );
  }, [
    fulfillment.shipping,
    fulfillment.date,
    fulfillment.time,
    fulfillment.quote,
    fulfillment.location,
    error,
    address,
    showDeliveryForm,
    isDoorDash,
    onDeliveryPayBlockedChange,
  ]);

  return (
    <Box>
      <Typography component="h2" variant="overline" color="textSecondary">
        FULFILLMENT
      </Typography>
      <Paper elevation={0}>
        <Box px={2} pt={2} pb={2}>
          <Box mb={2}>
            <RadioGroup
              name="fulfillmentShipping"
              value={fulfillment.shipping}
              onChange={(e) => {
                const next = e.target.value;
                if (next === "pickup") {
                  switchToPickup();
                } else if (next === "delivery" && options.shipping.delivery) {
                  onChange({
                    ...fulfillment,
                    shipping: "delivery",
                    quote: undefined,
                    location: {
                      ...(deliveryTimeslots ? { timeslots: deliveryTimeslots } : {}),
                      ...(deliveryRules ? { rules: deliveryRules } : {}),
                    },
                    time: "",
                  });
                  setAddress("");
                }
              }}
              sx={{
                display: "grid",
                width: "100%",
                gap: { xs: 1, sm: 1.5 },
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              {[
                { value: "pickup", title: "Pickup", disabled: false },
                {
                  value: "delivery",
                  title: options.shipping.delivery ? "Delivery" : "Delivery (not available)",
                  disabled: !options.shipping.delivery,
                },
              ].map((opt) => {
                const selected = fulfillment.shipping === opt.value;
                return (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    disabled={opt.disabled}
                    control={
                      <Radio
                        size="small"
                        sx={{
                          p: 0.5,
                          flexShrink: 0,
                          alignSelf: "center",
                        }}
                      />
                    }
                    label={
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          pr: { xs: 1, sm: 1.25 },
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          alignSelf: "stretch",
                        }}
                      >
                        <Typography
                          component="div"
                          variant="subtitle2"
                          fontWeight={600}
                          sx={{
                            fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.9375rem" },
                            lineHeight: 1.2,
                            maxWidth: "100%",
                            display: "block",
                          }}
                        >
                          {opt.title}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      m: 0,
                      mx: 0,
                      my: 0,
                      ml: 0,
                      mr: 0,
                      width: "100%",
                      minWidth: 0,
                      maxWidth: "100%",
                      display: "flex",
                      flexDirection: "row-reverse",
                      alignItems: "center",
                      justifyContent: "space-between",
                      boxSizing: "border-box",
                      alignSelf: "stretch",
                      height: "100%",
                      minHeight: { xs: 52, sm: 54 },
                      border: "2px solid",
                      borderColor: selected ? "primary.main" : theme.palette.divider,
                      borderRadius: 2,
                      px: { xs: 1.25, sm: 1.5, md: 1.75 },
                      py: { xs: 1, sm: 1.125, md: 1.25 },
                      columnGap: { xs: 1.25, sm: 1.5, md: 2 },
                      bgcolor: selected ? "transparent" : "grey.50",
                      cursor: opt.disabled ? "not-allowed" : "pointer",
                      transition: theme.transitions.create(["border-color", "background-color"], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      "& .MuiFormControlLabel-label": {
                        width: "100%",
                        minWidth: 0,
                        margin: 0,
                      },
                      "& .MuiButtonBase-root": {
                        margin: 0,
                        padding: { xs: "4px", sm: "6px" },
                      },
                    }}
                  />
                );
              })}
            </RadioGroup>
          </Box>
          <Box mt={3}>
            <Box px={1}>
              <Box mb={1}>
                <Typography variant="overline" color="textSecondary">
                  WHERE & WHEN?
                </Typography>
              </Box>
              {fulfillment.shipping === "pickup" && (
                <Box>
                  <RadioGroup
                    name="pickupLocation"
                    value={
                      fulfillment.location
                        ? pickupLocations.find((l) => matchPickupLocation(l, fulfillment.location))
                            ?.id || ""
                        : ""
                    }
                    onChange={(e) =>
                      onChange({
                        ...fulfillment,
                        shipping: "pickup",
                        location: pickupLocations.find((l) => l.id === e.target.value) || null,
                        time: "",
                      })
                    }
                  >
                    {pickupLocations.map((o) => {
                      return (
                        <Box key={o.id || o.street} py={0.5}>
                          <FormControlLabel
                            value={o.id}
                            control={<Radio />}
                            label={`${o.street}, ${o.city}, ${o.state} ${o.zip}`}
                          />
                        </Box>
                      );
                    })}
                  </RadioGroup>
                  {fulfillment.location && DATE_TIME}
                </Box>
              )}
              {fulfillment.shipping === "delivery" && (
                <Box>
                  {showDeliveryForm ? (
                    <Box component="form" onSubmit={onSubmit}>
                      {CONTENT}
                      {error && (
                        <Box mt={2}>
                          <Alert severity="error">{error}</Alert>
                        </Box>
                      )}
                      {deliveryActions}
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", gap: "15px" }}>
                      <Box>
                        <LocalShippingOutlinedIcon fontSize="large" sx={{ color: "#777" }} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle1">{fulfillment.location.street}</Typography>
                        {fulfillment.location.unit && (
                          <Typography variant="body1">{fulfillment.location.unit}</Typography>
                        )}
                        <Typography variant="subtitle1">
                          {fulfillment.location.city}, {fulfillment.location.state},{" "}
                          {fulfillment.location.zip}
                        </Typography>
                        <Box my={0.5}>
                          <Typography variant="body1">
                            {moment(fulfillment.date).format("ddd, MMM D, YYYY")} ●{" "}
                            {fulfillment.time || ""}
                          </Typography>
                        </Box>
                        <Link
                          component="a"
                          href="#"
                          role="button"
                          size="small"
                          onClick={(e) => {
                            e.preventDefault();
                            setForceDeliveryForm(true);
                          }}
                        >
                          change
                        </Link>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {processing && (
        <Backdrop open={true} style={{ zIndex: "2000", color: "#fff" }}>
          <CircularProgress color="inherit" />
          <Box p={2} sx={{ display: "block" }}>
            <Typography sx={{ fontSize: "1.2rem" }}>Checking Availability...</Typography>
            <Typography>This may take 5-10 seconds</Typography>
          </Box>
        </Backdrop>
      )}
    </Box>
  );
}
